/**
 * What a template is used for (SN-19067).
 *
 * `null` / `""` / absent all mean `ChartNote` server-side — there was no backfill, so
 * every template that predates SN-19067 has `type: null` and behaves exactly as before.
 * Never compare `template.type` directly; go through `getEffectiveTemplateType`
 * (`features/medical-scribe/components/scribe-template-type.util.ts`).
 */
export const AMBIENT_SCRIBE_TEMPLATE_TYPE = {
  CHART_NOTE: "ChartNote",
  AUDIOLOGY_REPORT: "AudiologyReport",
  AFTER_VISIT_SUMMARY: "AfterVisitSummary"
} as const;

export type AmbientScribeTemplateType =
  (typeof AMBIENT_SCRIBE_TEMPLATE_TYPE)[keyof typeof AMBIENT_SCRIBE_TEMPLATE_TYPE];

/**
 * Author shape on `created_by` / `updated_by`. Deliberately NOT `MinimalStaffMember` —
 * this endpoint omits `photo` and `signature` (image fields on a list endpoint), so the
 * fuller type would lie about what is present.
 */
export type ScribeTemplateAuthor = {
  id: number;
  first_name: string;
  last_name: string;
  name?: string;
  full_name?: string;
  suffix?: string | null;
  title?: number | null;
  is_active?: boolean;
};

/** An appointment type this template serves, as returned inside `appointment_types`. */
export type AmbientScribeTemplateAppointmentType = {
  id: number;
  name: string;
  general_type?: number;
  color?: string | null;
};

export interface AmbientScribeTemplate {
  id: number;
  name: string;
  template: string;
  practice_id: number;
  is_default: boolean | null;
  add_to_chart_notes: boolean | null;
  /**
   * The appointment types this template serves. `[]` when it serves none.
   *
   * **Optional is load-bearing, not laziness.** The field is `?query=`-gated, so `undefined`
   * means "we did not ask for it" while `[]` means "it genuinely has none". Anything writing
   * `appointment_type_ids` MUST distinguish them: sending `[]` clears every link, so a form
   * that initialised from an absent field would wipe real data. See the modal's submit.
   */
  appointment_types?: AmbientScribeTemplateAppointmentType[];
  /** `null` ≡ `ChartNote`. Absent entirely unless the request carried a `?query=`. */
  type?: AmbientScribeTemplateType | null;
  /** Free-form; the backend does not read it. Typed only for payload fidelity. */
  preferences?: Record<string, unknown> | null;
  /** Opt-in via `?query=`; `null` for templates the system created. */
  created_by?: ScribeTemplateAuthor | null;
  /** Opt-in via `?query=`; `null` for templates the system created. */
  updated_by?: ScribeTemplateAuthor | null;
  created_at: string;
  updated_at: string;
}

export interface AmbientScribeTemplateFilters {
  limit?: number;
  offset?: number;
  ordering?: string;
  search?: string;
  /** Comma-separated template types. `ChartNote` also matches `null` / `""` rows. */
  type?: string;
  /**
   * RestQL projection. Only send this behind AMBIENT_SCRIBE_TEMPLATE_TYPES — naming any
   * of the new nested objects against a backend that predates SN-19067 fails the whole
   * request with a 400.
   */
  query?: string;
}

export interface AmbientScribeTemplateCreateInput {
  name: string;
  template: string;
  is_default?: boolean | null;
  add_to_chart_notes?: boolean | null;
  /**
   * Omit entirely when AMBIENT_SCRIBE_TEMPLATE_TYPES is off. An older backend rejects the
   * unknown key with a 400 even when the value is `null`.
   */
  type?: AmbientScribeTemplateType;
  /**
   * Appointment types this template should serve. The backend reconciles: ids sent get linked,
   * previously-linked ids that are absent get unlinked.
   *
   * **Omitting the key and sending `[]` are NOT the same.** Absent / `null` / `""` leave links
   * untouched; `[]` clears every one. Only send it when the current links are actually known.
   * Also: an appointment type serves only one template, so linking one that belongs elsewhere
   * MOVES it and the previous owner silently loses it. ChartNote templates only.
   */
  appointment_type_ids?: number[];
}

export interface AmbientScribeTemplateUpdateInput {
  name?: string;
  template?: string;
  is_default?: boolean | null;
  add_to_chart_notes?: boolean | null;
  /** Same flag gating as the create input. A PATCH that omits it never changes the type. */
  type?: AmbientScribeTemplateType;
  /** See the create input — absent ≠ `[]`, and linking is a move. */
  appointment_type_ids?: number[];
}

/** Body for the async "draft a template with AI" request. */
export interface GenerateScribeTemplateInput {
  clinic_id?: number;
  appointment_type_id?: number;
  /** Keeps its name for both types — for AudiologyReport, paste a report summary here. */
  chart_note_example?: string;
  format?: "html";
  type?: AmbientScribeTemplateType;
}

export type ScribeTemplateGenerationStatusValue =
  | "processing"
  | "success"
  | "failed";

/**
 * Response shape shared by the generate POST and the status-poll GET.
 * - `processing` carries `task_id` (poll it),
 * - `success` carries `result` (the template HTML),
 * - `failed` carries `error` (a human-readable message).
 */
export interface ScribeTemplateGenerationStatus {
  status: ScribeTemplateGenerationStatusValue;
  task_id?: string;
  result?: string;
  error?: string;
}
